// jq path syntax, nested lookups, and flattening a sample payload into paths.

/** Converts a path array like `["device", "gps", "lat"]` to jq syntax: `.device.gps.lat` */
export function jqPath(pathSegments: string[]): string {
  return pathSegments
    .map(
      (seg) =>
        /^\d+$/.test(seg)
          ? `[${seg}]` // array index: [0], [1], ...
          : /^\w+$/.test(seg)
            ? `.${seg}` // simple key: .name
            : `."${seg}"`, // special chars: ."frm-payload"
    )
    .join('');
}

/** Safely traverses a nested object following `pathSegments`. Returns `undefined` if any key is missing. */
export function valAt(obj: any, pathSegments: string[]): any {
  let current = obj;
  for (const key of pathSegments) {
    if (current == null) return undefined;
    current = current[key];
  }
  return current;
}

/** Defines `key` as an own property, so keys like `__proto__` never reach the prototype chain. */
function setOwn(obj: any, key: string, value: any): void {
  Object.defineProperty(obj, key, {
    value,
    writable: true,
    enumerable: true,
    configurable: true,
  });
}

/** Sets `value` at a dotted path in `obj`, creating intermediate objects as needed.
 *  Walks and writes own properties only: the keys come from a saved filter, and a key
 *  like `__proto__.x` must not write onto the global prototype. */
export function setAtDotted(obj: any, dottedKey: string, value: any): void {
  const segments = dottedKey.split('.');
  let current = obj;
  for (let i = 0; i < segments.length - 1; i++) {
    const seg = segments[i];
    const next = Object.prototype.hasOwnProperty.call(current, seg)
      ? current[seg]
      : undefined;
    if (next == null || typeof next !== 'object') setOwn(current, seg, {});
    current = current[seg];
  }
  setOwn(current, segments[segments.length - 1], value);
}

/**
 * Replaces the contents of every string literal with spaces (quotes kept, length preserved),
 * so regex-based structural checks can't be fooled by text inside user data
 * (e.g. a condition value containing `as $x` or `word: .path`).
 */
export function maskStrings(s: string): string {
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === '"') {
      out += '"';
      i++;
      let contentLength = 0;
      while (i < s.length && s[i] !== '"') {
        if (s[i] === '\\') {
          i++;
          contentLength++;
        }
        contentLength++;
        i++;
      }
      out += ' '.repeat(contentLength) + (i < s.length ? '"' : '');
    } else {
      out += ch;
    }
  }
  return out;
}

/**
 * Splits a jq path string into segments, handling quoted keys like `."frm-payload"`.
 * Input: `uplink_message.locations."frm-payload".latitude`
 * Output: `["uplink_message", "locations", "frm-payload", "latitude"]`
 */
export function splitJqPath(pathStr: string): string[] {
  const segments: string[] = [];
  let cursor = 0;
  while (cursor < pathStr.length) {
    if (pathStr[cursor] === '.') {
      cursor++;
      continue;
    }
    if (pathStr[cursor] === '[') {
      // Bracket notation: [0], ["key"]
      const closeBracket = pathStr.indexOf(']', cursor + 1);
      if (closeBracket === -1) break;
      let inner = pathStr.slice(cursor + 1, closeBracket);
      // Strip quotes if present: ["key"] → key
      if (inner.startsWith('"') && inner.endsWith('"'))
        inner = inner.slice(1, -1);
      segments.push(inner);
      cursor = closeBracket + 1;
    } else if (pathStr[cursor] === '"') {
      const closeQuote = pathStr.indexOf('"', cursor + 1);
      if (closeQuote === -1) break;
      segments.push(pathStr.slice(cursor + 1, closeQuote));
      cursor = closeQuote + 1;
    } else {
      let end = cursor;
      while (
        end < pathStr.length &&
        pathStr[end] !== '.' &&
        pathStr[end] !== '"' &&
        pathStr[end] !== '['
      )
        end++;
      segments.push(pathStr.slice(cursor, end));
      cursor = end;
    }
  }
  return segments;
}

/** Recursively flattens a nested object/array into an array of `{ path, value }` entries for every key (including intermediate objects and array items). */
export function flatPaths(
  obj: any,
  prefix: string[] = [],
): { path: string[]; value: any }[] {
  const result: { path: string[]; value: any }[] = [];
  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      const fullPath = [...prefix, String(index)];
      result.push({ path: fullPath, value: item });
      if (item && typeof item === 'object') {
        result.push(...flatPaths(item, fullPath));
      }
    });
  } else if (obj && typeof obj === 'object') {
    for (const [key, value] of Object.entries(obj)) {
      const fullPath = [...prefix, key];
      result.push({ path: fullPath, value });
      if (value && typeof value === 'object') {
        result.push(...flatPaths(value, fullPath));
      }
    }
  }
  return result;
}

/** Returns the jq key name for the "additional" bag depending on object type. */
export function addlKey(objectType: string): string {
  return objectType === 'ev' ? 'event_details' : 'additional';
}
